/**
 * La carte d'un entrepreneur, et sa fiche complète — le cœur du côté
 * « Tinder B2B » : un score qui n'est jamais nu, une raison de s'appeler, un
 * brise-glace, et deux gestes : voir, rencontrer.
 */
import { useState } from 'react';
import { ExternalLink, Sparkles, Users } from 'lucide-react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Btn, Card, Modal, SectionLabel } from '../../admin/ui.tsx';
import { Etoile } from '../../composants/Etoile.tsx';
import { libelleCa, lienSuivi, nomDe, reseauDe, type Carte, type Fiche, type Relation } from './types.ts';

const LIBELLE_RELATION: Record<Relation, string> = {
  aucune: 'Je veux les rencontrer',
  demande_envoyee: 'Demande envoyée',
  demande_recue: 'Ils veulent vous rencontrer',
  match: 'Match',
  declinee: 'Déclinée',
};

export function Score({ score }: { score: number }) {
  if (score <= 0) return null;
  return <span className="text-match-gradient shrink-0 text-[14px] font-medium">{Math.round(score * 100)} %</span>;
}

function BoutonRencontre({
  carte,
  evenementId,
  onChange,
}: {
  carte: Carte;
  evenementId?: string | null;
  onChange?: () => void;
}) {
  const [etat, setEtat] = useState<Relation>(carte.relation);
  const [enCours, setEnCours] = useState(false);

  const demander = async () => {
    setEnCours(true);
    const r = await envoyer<{ statut: 'match' | 'demande_envoyee' }>(
      `/api/compte/profils/${carte.personne_id}/demander`,
      { evenementId: evenementId ?? null },
    );
    setEnCours(false);
    if (r.donnees) {
      setEtat(r.donnees.statut === 'match' ? 'match' : 'demande_envoyee');
      onChange?.();
    }
  };

  const repondre = async (accepter: boolean) => {
    if (!carte.match_id) return;
    setEnCours(true);
    const r = await envoyer(`/api/compte/demandes/${carte.match_id}/repondre`, { accepter });
    setEnCours(false);
    if (!r.erreur) {
      setEtat(accepter ? 'match' : 'declinee');
      onChange?.();
    }
  };

  if (etat === 'match') return <Badge filled>Match ✓</Badge>;
  if (etat === 'declinee') return <Badge>Déclinée</Badge>;
  if (etat === 'demande_envoyee') return <Badge>Demande envoyée</Badge>;
  if (etat === 'demande_recue') {
    return (
      <span className="flex gap-2">
        <Btn variant="primary" disabled={enCours} onClick={() => void repondre(true)}>
          Accepter
        </Btn>
        <Btn disabled={enCours} onClick={() => void repondre(false)}>
          Passer
        </Btn>
      </span>
    );
  }
  return (
    <Btn variant="primary" disabled={enCours} onClick={() => void demander()}>
      {enCours ? '…' : LIBELLE_RELATION.aucune}
    </Btn>
  );
}

export function CarteEntrepreneur({
  carte,
  evenementId,
  onChange,
  compacte = false,
}: {
  carte: Carte;
  evenementId?: string | null;
  onChange?: () => void;
  compacte?: boolean;
}) {
  const [fiche, setFiche] = useState<Fiche | null>(null);
  const [ouverte, setOuverte] = useState(false);

  const ouvrir = async () => {
    setOuverte(true);
    if (fiche) return;
    const r = await appelerApi<Fiche>(`/api/compte/profils/${carte.personne_id}`);
    if (r.donnees) setFiche(r.donnees);
  };

  return (
    <>
      <Card className="flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <button onClick={() => void ouvrir()} className="min-w-0 text-left">
            <p className="flex flex-wrap items-center gap-2 text-[15px] text-blanc">
              {nomDe(carte)}
              <Etoile premium={carte.premium} />
              {carte.nouveau && <Badge>Nouveau</Badge>}
            </p>
            <p className="mt-0.5 truncate text-[13px] text-fumee">
              {[carte.fonction, carte.entreprise].filter(Boolean).join(' · ') || '—'}
            </p>
            {!compacte && (
              <p className="mt-0.5 text-[12px] text-fumee">
                {[carte.secteur, libelleCa(carte.ca_annuel)].filter(Boolean).join(' · ')}
              </p>
            )}
          </button>
          <Score score={carte.score} />
        </div>

        <p className="mt-3 text-[13px] leading-relaxed text-blanc">{carte.explication}</p>
        {carte.brise_glace && !compacte && (
          <p className="mt-2 flex items-start gap-1.5 text-[13px] leading-relaxed text-fumee">
            <Sparkles size={13} strokeWidth={1.5} className="mt-0.5 shrink-0" />
            {carte.brise_glace}
          </p>
        )}

        {!compacte && (carte.pitch || carte.services) && (
          <p className="mt-3 line-clamp-3 text-[13px] leading-relaxed text-fumee">
            <span className="label-caps mr-1 text-fumee/70">Propose</span> {carte.pitch ?? carte.services}
          </p>
        )}

        {!compacte && carte.interets.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {carte.interets.slice(0, 6).map((i) => (
              <span
                key={i}
                className={`rounded-full px-2.5 py-1 text-[12px] ${
                  carte.interets_communs.some((c) => c.toLowerCase() === i.toLowerCase())
                    ? 'bg-blanc text-void'
                    : 'border border-graphite text-fumee'
                }`}
              >
                {i}
              </span>
            ))}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-graphite pt-3">
          <button onClick={() => void ouvrir()} className="text-[13px] text-fumee underline hover:text-blanc">
            Voir le profil
          </button>
          <BoutonRencontre carte={carte} evenementId={evenementId} onChange={onChange} />
        </div>
      </Card>

      {ouverte && (
        <Modal title={nomDe(carte)} onClose={() => setOuverte(false)}>
          {fiche ? (
            <FicheDetaillee fiche={fiche} evenementId={evenementId} onChange={onChange} />
          ) : (
            <p className="text-[14px] text-fumee">Chargement…</p>
          )}
        </Modal>
      )}
    </>
  );
}

function FicheDetaillee({ fiche: f, evenementId, onChange }: { fiche: Fiche; evenementId?: string | null; onChange?: () => void }) {
  const [prep, setPrep] = useState<{ source: 'ia' | 'modele'; texte: string } | null>(null);
  const [prepEnCours, setPrepEnCours] = useState(false);

  const preparer = async () => {
    setPrepEnCours(true);
    const r = await envoyer<{ source: 'ia' | 'modele'; texte: string }>(`/api/compte/profils/${f.personne_id}/preparer`, {});
    setPrepEnCours(false);
    if (r.donnees) setPrep(r.donnees);
  };

  const liens = [
    f.site_web && { libelle: 'Site', url: f.site_web },
    f.linkedin && { libelle: 'LinkedIn', url: f.linkedin },
    ...f.liens,
  ].filter(Boolean) as { libelle: string; url: string }[];

  return (
    <div className="flex flex-col gap-5 text-[14px]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-fumee">{[f.fonction, f.entreprise].filter(Boolean).join(' · ')}</p>
          <p className="text-[12px] text-fumee">{[f.secteur, libelleCa(f.ca_annuel), f.origine_geographique].filter(Boolean).join(' · ')}</p>
        </div>
        <Score score={f.score} />
      </div>

      <div className="rounded-[8px] border border-graphite p-3">
        <p className="text-blanc">{f.explication}</p>
        {f.brise_glace && <p className="mt-1.5 text-fumee">{f.brise_glace}</p>}
      </div>

      {f.pitch && <Section titre="Pitch">{f.pitch}</Section>}
      {!f.pitch && f.services && <Section titre="Propose">{f.services}</Section>}
      {f.client_ideal && <Section titre="Client idéal">{f.client_ideal}</Section>}
      {f.recherche && <Section titre="Cherche">{f.recherche}</Section>}
      {f.bio && <Section titre="En deux mots">{f.bio}</Section>}
      {f.situation && <Section titre="Situation">{f.situation}</Section>}

      {(f.forces.length > 0 || f.sujets_conversation.length > 0) && (
        <div className="grid gap-4 sm:grid-cols-2">
          {f.forces.length > 0 && (
            <div>
              <SectionLabel>Forces</SectionLabel>
              <Etiquettes valeurs={f.forces} communes={f.forces_complementaires.map((x) => x.sienne)} />
            </div>
          )}
          {f.sujets_conversation.length > 0 && (
            <div>
              <SectionLabel>Hors travail</SectionLabel>
              <Etiquettes valeurs={f.sujets_conversation} communes={f.sujets_communs} />
            </div>
          )}
        </div>
      )}

      {f.equipe.length > 0 && (
        <div>
          <SectionLabel>Son équipe</SectionLabel>
          <ul className="mt-2 flex flex-col gap-2">
            {f.equipe.map((m, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2 text-[13px]">
                <Users size={13} strokeWidth={1.5} className="text-fumee" />
                <span className="text-blanc">{[m.prenom, m.nom].filter(Boolean).join(' ')}</span>
                {m.fonction && <span className="text-fumee">· {m.fonction}</span>}
                {m.liens.map((l) => (
                  <a key={l.url} href={lienSuivi(f.personne_id, l.url)} target="_blank" rel="noreferrer" className="text-fumee underline hover:text-blanc">
                    {l.libelle || reseauDe(l.url)}
                  </a>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}

      {liens.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {liens.map((l) => (
            <a
              key={l.url}
              href={lienSuivi(f.personne_id, l.url)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[13px] text-fumee hover:text-blanc"
            >
              {l.libelle || reseauDe(l.url)} <ExternalLink size={12} strokeWidth={1.5} />
            </a>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-graphite pt-4">
        <div className="flex flex-wrap items-center gap-3">
          <BoutonRencontre carte={f} evenementId={evenementId} onChange={onChange} />
          {f.contact_visible && f.email && (
            <a
              href={`mailto:${f.email}?subject=${encodeURIComponent('Prendre rendez-vous — EventMatch.pro')}&body=${encodeURIComponent(`Bonjour ${f.prenom ?? ''},\n\nNous sommes tous deux sur EventMatch.pro et ${f.explication.toLowerCase()}\n\nSeriez-vous disponible pour un appel de quinze minutes ?\n\n`)}`}
              className="text-[13px] text-blanc underline"
            >
              Proposer un rendez-vous
            </a>
          )}
          {!f.contact_visible && (
            <span className="text-[12px] text-fumee">Coordonnées visibles après match.</span>
          )}
        </div>
        <Btn disabled={prepEnCours} onClick={() => void preparer()}>
          <Sparkles size={13} strokeWidth={1.5} className="mr-1.5 inline" />
          {prepEnCours ? '…' : 'Préparer le rendez-vous'}
        </Btn>
      </div>

      {prep && (
        <div className="rounded-[8px] bg-void p-4">
          <p className="label-caps text-fumee">
            {prep.source === 'ia' ? 'Préparé par l’IA' : 'Préparé depuis vos profils (IA non configurée)'}
          </p>
          <pre className="mt-2 whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-blanc">{prep.texte}</pre>
        </div>
      )}
    </div>
  );
}

function Section({ titre, children }: { titre: string; children: string }) {
  return (
    <div>
      <SectionLabel>{titre}</SectionLabel>
      <p className="mt-1.5 leading-relaxed text-fumee">{children}</p>
    </div>
  );
}

function Etiquettes({ valeurs, communes }: { valeurs: string[]; communes: string[] }) {
  const c = new Set(communes.map((x) => x.toLowerCase()));
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {valeurs.map((v) => (
        <span
          key={v}
          className={`rounded-full px-2.5 py-1 text-[12px] ${c.has(v.toLowerCase()) ? 'bg-blanc text-void' : 'border border-graphite text-fumee'}`}
        >
          {v}
        </span>
      ))}
    </div>
  );
}
